import { Carousel, LoadMoreIndicator } from "client/Components";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { Guid } from "client_legacy/ndaw/guid";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import styles from "./GridLayout.module.less";
import {
	useHubItemLiveInformation,
	useHubCardSizeInformation,
	HubItemLiveInformationProvider,
	HubCardSizeInformation,
	HubCardWidth,
	HubCardHeight
} from "./hooks";
import {
	CardProps,
	HubTileCategoryIds
} from "./common";
import { EmptyResult, ErrorResult } from "./NoResults";
import { DimensionToRender } from "../../RenderState/common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	AlertManager,
	useAlerts,
	useValueEqualMemo
} from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { HubItemTileResourceKind } from "client/Api/Workspace/workspace";

export type GridLayoutProps<TItem extends { id: number; }, TInterests> = {
	tabId: Guid;
	categoryIds: HubTileCategoryIds;
	dimension: DimensionToRender;
	provider: HubItemLiveInformationProvider<TItem, TInterests>;
	interestsFactory: (sizeInfo: HubCardSizeInformation) => TInterests;
	cardComponent: {
		(props: CardProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
};

export const GridLayout = <TItem extends { id: number; }, TInterests>(
	props: GridLayoutProps<TItem, TInterests>
): ReactElement | null => {
	const {
		tabId,
		categoryIds,
		dimension,
		provider,
		interestsFactory,
		cardComponent,
		onOpen,
		timezone
	} = props;

	const [additionalPages, setAdditionalPages] = useState<readonly AdditionalPage<TItem>[]>([]);
	const additionalPagesAbortControllerRef: RefObject<AbortController | null> = useRef<AbortController>(null);

	const alertManager: AlertManager = useAlerts();
	const sizeInfo: HubCardSizeInformation = useHubCardSizeInformation(dimension);

	const {
		height: slideHeight,
		width: slideWidth
	} = sizeInfo;

	const unstableInterests: TInterests = interestsFactory(sizeInfo);
	const interests = useValueEqualMemo(
		(): TInterests => unstableInterests,
		[unstableInterests]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(categoryIds);
			hasImplicitDependencyOn(provider);
			additionalPagesAbortControllerRef.current
				?.abort();
			setAdditionalPages([]);
		},
		[categoryIds, provider]
	);

	const [loadingState, refreshHandler] = useHubItemLiveInformation(
		provider,
		tabId,
		{
			kind: HubItemTileResourceKind.CategoryIds,
			categoryIds
		},
		additionalPages.length !== 0
			? undefined
			: interests
	);

	useEffect(
		(): (() => void) => {
			return () => additionalPagesAbortControllerRef.current
				?.abort();
		},
		[]
	);

	const scrollHandler = useCallback(
		(scrollLeft: number): void => {
			if (scrollLeft <= 0) {
				additionalPagesAbortControllerRef.current
					?.abort();
				setAdditionalPages([]);
			}
		},
		[]
	);

	const {
		isLoading: isLiveInformationLoading,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		page,
		lastPinnedItem
	} = loadingState;

	const isLoading: boolean = additionalPages[additionalPages.length - 1]
		?.isLoading ?? isLiveInformationLoading;

	const lastAdditionalPage: AdditionalPage<TItem> | undefined = additionalPages[additionalPages.length - 1];
	let hasMore: boolean = page?.hasMore ?? false;

	if (lastAdditionalPage) {
		hasMore = lastAdditionalPage.page
			?.hasMore ?? false;
	}

	const loadMoreHandler = useCallback(
		async (): Promise<void> => {
			const lastAdditionalPage: AdditionalPage<TItem> | undefined = additionalPages[additionalPages.length - 1];
			let lastPage: LazyLoadPageResult<TItem> | undefined = loadingState.page;
			if (lastAdditionalPage) {
				if (lastAdditionalPage.isLoading) {
					throw new Error("Cannot load more while still loading the additional page");
				}

				lastPage = lastAdditionalPage.page;
			}

			if (!lastPage?.hasMore) {
				throw new Error("Cannot load more because the last page has no more items");
			}

			const { items } = lastPage;
			const lastLoadedItem: TItem = items[items.length - 1];

			setAdditionalPages(
				x => [
					...x,
					{
						isLoading: true,
						page: undefined
					}
				]
			);

			try {
				additionalPagesAbortControllerRef.current
					?.abort();

				const cancellation = new AbortController();
				additionalPagesAbortControllerRef.current = cancellation;

				const page: LazyLoadPageResult<TItem> = await provider
					.loadMoreItems(
						categoryIds,
						interests,
						lastLoadedItem,
						cancellation.signal
					);

				setAdditionalPages(
					x => {
						const copy: AdditionalPage<TItem>[] = [...x];

						const lastPage: AdditionalPage<TItem> | undefined = copy[copy.length - 1];

						if (lastPage) {
							if (!lastPage.isLoading) {
								console.warn("The last page is not loading anymore");
							} else {
								copy.pop();
								copy.push({
									isLoading: false,
									page
								});
							}
						}

						return copy;
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			additionalPages,
			alertManager,
			categoryIds,
			interests,
			loadingState.page,
			provider
		]
	);

	const slides = useMemo(
		(): readonly SlidesWithItems<TItem>[] => {
			if (page?.items) {
				let items: TItem[] = [
					page,
					...additionalPages.map(x => x.page)
						.filter(isNotNil)
				]
					.flatMap(x => x.items);

				if (lastPinnedItem) {
					items = items
						.filter(
							x => x.id !== lastPinnedItem.id
						);

					items.unshift(lastPinnedItem);
				}

				return getSlidesWithItemLayoutInfo(
					items,
					{
						height: slideHeight,
						width: slideWidth
					}
				);
			}

			return [];
		},
		[
			page,
			additionalPages,
			lastPinnedItem,
			slideWidth,
			slideHeight
		]
	);

	if (hasTooManyConsecutiveErrors) {
		return <TileRefresh onRefresh={refreshHandler} />;
	}

	if (hasGlobalFailure) {
		return <ErrorResult itemKind={provider.itemKind} onRefresh={refreshHandler} />;
	}

	if (!page) {
		return null;
	}

	if (slides.length === 0) {
		return <EmptyResult itemKind={provider.itemKind} categoryIds={categoryIds} />;
	}

	return (
		<Carousel isNavigationBarHidden onScroll={scrollHandler}>
			{slides.map((x, i) => {
				const isLastSlide: boolean = i === slides.length - 1;
				const isLastSlidePartiallyFilled = isLastSlide && x.isPartiallyFilled;

				const slideElement: ReactElement = (
					<Slide
						key={`slider-${i}`}
						items={x.items}
						cardComponent={cardComponent}
						onOpen={onOpen}
						timezone={timezone}
					/>
				);

				const loadMoreElement: ReactElement = (
					<div className={styles.loadMoreContainer} key={`load-more-${i}`}>
						<LoadMoreIndicator
							hasMoreItems={hasMore}
							isLoadingData={isLoading}
							onRequestMoreItems={loadMoreHandler}
						/>
					</div>
				);

				if (!hasMore && !isLoading) {
					return slideElement;
				}

				if (isLastSlidePartiallyFilled) {
					return loadMoreElement;
				}
				return (
					<Fragment key={i}>
						{slideElement}
						{isLastSlide && hasMore && loadMoreElement}
					</Fragment>
				);
			})}
		</Carousel>
	);
};
GridLayout.displayName = "GridLayout";

type SlideProps<TItem extends { id: number; }> = {
	items: readonly ItemWithLayoutInfo<TItem>[];
	cardComponent: {
		(props: CardProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
};

const Slide = <TItem extends { id: number; },>(props: SlideProps<TItem>): ReactElement => {
	const {
		items,
		cardComponent: CardComponent,
		onOpen,
		timezone
	} = props;

	return (
		<div
			className={styles.grid}
			style={{
				"--hub-tile-slide-column-size": gridColumnSize,
				"--hub-tile-slide-row-size": gridRowSize,
			}}
		>
			{items.map((x) => {
				const {
					spans,
					size,
					shouldHideSecondaryInfo
				} = x.layoutInfo;

				return (
					<div
						key={x.item.id}
						style={{
							"--hub-tile-slide-col-span": spans[0],
							"--hub-tile-slide-row-span": spans[1]
						}}
					>
						<CardComponent
							item={x.item}
							size={size}
							shouldHideSecondaryInfo={shouldHideSecondaryInfo}
							onOpen={onOpen}
							timezone={timezone}
						/>
					</div>
				);
			})}
		</div>
	);
};
Slide.displayName = "Slide";

type SizeInfo = {
	width: HubCardWidth;
	height: HubCardHeight;
};

type Spans = [colSpan: number, rowSpan: number];

type ItemLayoutInfo = {
	spans: Spans;
	shouldHideSecondaryInfo: boolean;
	size: "small" | "large";
};

type ItemWithLayoutInfo<TItem extends { id: number; }> = {
	item: TItem;
	layoutInfo: ItemLayoutInfo;
};

type SlidesWithItems<TItem extends { id: number; }> = {
	isPartiallyFilled: boolean;
	items: readonly ItemWithLayoutInfo<TItem>[];
};

function getSlidesWithItemLayoutInfo<TItem extends { id: number; }>(
	items: readonly TItem[],
	sizeInfo: SizeInfo
): readonly SlidesWithItems<TItem>[] {
	const remaining: TItem[] = [...items];
	const result: SlidesWithItems<TItem>[] = [];

	let index: number = 0;

	while (remaining.length > 0) {
		const isFirstSlide: boolean = index === 0;

		switch (sizeInfo.width) {
			case HubCardWidth.UltraNarrow:
			case HubCardWidth.Narrow:
			case HubCardWidth.Regular: {
				const maxNumberOfItems: TileSliderItemAmount = sizeInfo.width === HubCardWidth.UltraNarrow
					? 1
					: isFirstSlide
						? 3
						: 4;

				const items: TItem[] = remaining.splice(
					0,
					maxNumberOfItems
				);

				if (!isSliderItemAmount(items.length)) {
					throw new Error("number of items is not supported");
				}

				const spanSelector: TileSliderItemAmount = isFirstSlide
					? items.length
					: maxNumberOfItems;

				const allSpans: readonly Spans[] = spansToItemNumberMapInSmallTile[spanSelector];

				const itemsWithLayoutInfo: readonly ItemWithLayoutInfo<TItem>[] = items.map(
					(x, i): ItemWithLayoutInfo<TItem> => {
						const spans: Spans = allSpans[i];
						const isItemBig: boolean = sizeInfo.width !== HubCardWidth.UltraNarrow
							&& getPercentageOfItem(spans) >= 50;

						return {
							item: x,
							layoutInfo: {
								spans,
								shouldHideSecondaryInfo: !isItemBig,
								size: isItemBig ? "large" : "small"
							}
						};
					});

				result.push({
					isPartiallyFilled: items.length < maxNumberOfItems,
					items: itemsWithLayoutInfo
				});

				break;
			}

			case HubCardWidth.Wide: {
				const maxNumberOfItems: WideTileSliderItemAmount = !isFirstSlide
					? 8
					: sizeInfo.height === HubCardHeight.Long
						? 6
						: 5;

				const items: TItem[] = remaining.splice(
					0,
					maxNumberOfItems
				);

				if (!isWideSliderItemAmount(items.length)) {
					throw new Error("number of items is not supported");
				}

				const spanSelector: WideTileSliderItemAmount = isFirstSlide
					? items.length
					: maxNumberOfItems;

				const allSpans: readonly Spans[] = spansToItemNumberMapInWideTile[spanSelector];

				const itemsWithLayoutInfo: readonly ItemWithLayoutInfo<TItem>[] = items.map(
					(x, i): ItemWithLayoutInfo<TItem> => {
						const spans: Spans = allSpans[i];

						const isItemBig: boolean = getPercentageOfItem(spans) >= 25;

						return {
							item: x,
							layoutInfo: {
								spans,
								shouldHideSecondaryInfo: false,
								size: isItemBig ? "large" : "small"
							}
						};
					});

				result.push({
					isPartiallyFilled: items.length < maxNumberOfItems,
					items: itemsWithLayoutInfo
				});

				break;
			}
			default:
				never(sizeInfo.width);
		}
		index++;
	}

	return result;
}

function getPercentageOfItem(spans: Spans): number {
	const tileArea: number = gridColumnSize * gridRowSize;

	return (spans[0] * spans[1] / tileArea) * 100;
}

type TileSliderItemAmount = 1 | 2 | 3 | 4;
type WideTileSliderItemAmount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const spansToItemNumberMapInWideTile: Record<WideTileSliderItemAmount, readonly Spans[]> = {
	1: [[4 ,2]],
	2: [[2, 2], [2, 2]],
	3: [[2, 2], [2, 1], [2, 1]],
	4: [[2, 2], [2, 1], [1, 1], [1, 1]],
	5: [[2, 2], [1, 1], [1, 1], [1, 1], [1, 1]],
	6: [[2, 1], [2, 1], [1, 1], [1, 1], [1, 1], [1, 1]],
	7: [[2, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1]], // 7 is not functionally used, but it helps us get ride of undefined
	8: [[1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1]]
};

const spansToItemNumberMapInSmallTile: Record<TileSliderItemAmount, readonly Spans[]> = {
	1: [[4 ,2]],
	2: [[4, 1], [4, 1]],
	3: [[4, 1], [2, 1], [2, 1]],
	4: [[2, 1], [2, 1], [2, 1], [2, 1]]
};

function isSliderItemAmount(amount: number): amount is TileSliderItemAmount {
	return amount >= 1 && amount <= 4;
}

function isWideSliderItemAmount(amount: number): amount is WideTileSliderItemAmount {
	return amount >= 1 && amount <= 8;
}

const gridColumnSize = 4;
const gridRowSize = 2;

type AdditionalPage<TItem extends { id: number; }> = {
	isLoading: boolean;
	page: LazyLoadPageResult<TItem> | undefined;
};
